import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router";

import { Container } from "../components/Container";
import { SEO } from "../components/SEO";
import {
  createGuestEsimCheckout,
  fetchEsimPackageById,
  type EsimPackage,
} from "../lib/api";
import {
  cardClass,
  errorBoxClass,
  formatDataLabel,
  formatEsimPrice,
  inputClass,
  localizedCountryLabel,
  panelClass,
} from "../lib/esimCatalog";
import { getLocalizedPackageDisplayTitle } from "../lib/esimFairUsage";
import { useLocale } from "../hooks/useLocale";

type FormState = "idle" | "submitting" | "error";

export function EsimCheckout() {
  const { t } = useTranslation();
  const { pathFor, language } = useLocale();
  const location = useLocation();
  const [params] = useSearchParams();
  const packageId = params.get("packageId") ?? "";

  const initialPackage = (location.state as { package?: EsimPackage } | null)?.package;
  const [pkg, setPkg] = useState<EsimPackage | null>(initialPackage ?? null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [createAccount, setCreateAccount] = useState(true);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formState, setFormState] = useState<FormState>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (pkg || !packageId) return;
    let cancelled = false;
    (async () => {
      try {
        const match = await fetchEsimPackageById(packageId);
        if (cancelled) return;
        if (!match) {
          setError(t("esim.load_package_error"));
          return;
        }
        setPkg(match);
      } catch {
        if (!cancelled) setError(t("esim.load_package_error"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packageId, pkg, t]);

  const canSubmit = useMemo(
    () =>
      Boolean(
        pkg &&
          firstName.trim() &&
          lastName.trim() &&
          email.trim().includes("@") &&
          acceptTerms &&
          formState !== "submitting",
      ),
    [pkg, firstName, lastName, email, acceptTerms, formState],
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pkg || !canSubmit) return;

    setFormState("submitting");
    setError(null);

    const origin = window.location.origin;
    const successUrl = `${origin}${pathFor("/esim/success")}`;
    const cancelUrl = `${origin}${pathFor(`/esim/checkout?packageId=${encodeURIComponent(pkg.packageId)}`)}`;

    try {
      const result = await createGuestEsimCheckout({
        packageId: pkg.packageId,
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
        createAccount,
        successUrl,
        cancelUrl,
      });

      if (!result.checkoutUrl) {
        throw new Error(t("esim.checkout_url_missing"));
      }

      sessionStorage.setItem(
        "albavox.guestEsim",
        JSON.stringify({
          orderId: result.orderId,
          email: email.trim(),
          packageId: pkg.packageId,
        }),
      );

      window.location.href = result.checkoutUrl;
    } catch (err) {
      setFormState("error");
      setError(err instanceof Error ? err.message : t("esim.checkout_failed"));
    }
  }

  const dataLabel = pkg
    ? pkg.isUnlimited
      ? t("esim.unlimited")
      : formatDataLabel(pkg)
    : "";
  const packageTitle = pkg
    ? getLocalizedPackageDisplayTitle(pkg, t)
    : "";
  const placeLabel = pkg
    ? localizedCountryLabel(pkg.countryCode, pkg.countryName, {
        locale: language.acceptLanguageCode,
        t,
        geography: pkg.geography,
      }) || t("esim.travel")
    : "";

  return (
    <main className="min-h-screen bg-bg-base pt-28 sm:pt-32">
      <SEO
        title={t("esim.meta_title_checkout")}
        description={t("esim.meta_description_checkout")}
        path="/esim/checkout"
      />

      <Container size="md">
        <article className="py-12 sm:py-16">
          <Link
            to={
              packageId
                ? pathFor(
                    `/esim/package?packageId=${encodeURIComponent(packageId)}`,
                  )
                : pathFor("/esim/shop")
            }
            state={pkg ? { package: pkg } : undefined}
            className="text-sm font-semibold text-brand-500"
          >
            {t("esim.back_to_details")}
          </Link>

          <header className="mt-6 max-w-2xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-brand-500">
              {t("esim.checkout_eyebrow")}
            </p>
            <h1 className="hero-headline text-text-heading">
              {t("esim.checkout_title")}
            </h1>
            <p className="mt-4 text-lg text-text-muted">
              {t("esim.checkout_subtitle")}
            </p>
          </header>

          {pkg ? (
            <div className={`mt-8 ${cardClass}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-brand-500">
                    {dataLabel}
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-text-heading">
                    {packageTitle || t("esim.esim_plan")}
                  </h2>
                  <p className="text-sm text-text-muted">
                    {placeLabel} ·{" "}
                    {t("esim.days", { count: pkg.durationDays })}
                  </p>
                </div>
                <p className="text-2xl font-bold text-text-heading">
                  {formatEsimPrice(pkg.price)}
                </p>
              </div>
            </div>
          ) : (
            <p className="mt-8 text-text-muted">
              {packageId
                ? t("esim.loading_package")
                : t("esim.select_package_first")}
            </p>
          )}

          <form onSubmit={onSubmit} className={`mt-8 grid gap-4 ${panelClass} p-5`}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("esim.first_name")} required>
                <input
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label={t("esim.last_name")} required>
                <input
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label={t("esim.email")} required>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label={t("esim.phone_optional")}>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
              />
            </Field>

            <label className="flex items-start gap-3 rounded-xl border border-border-soft bg-bg-subtle/50 p-4 text-sm dark:bg-bg-base/40">
              <input
                type="checkbox"
                checked={createAccount}
                onChange={(e) => setCreateAccount(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="inline-flex flex-wrap items-center gap-2 font-semibold text-text-heading">
                  {t("esim.create_account_title")}
                  <span className="rounded-full bg-bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-text-muted dark:bg-bg-subtle">
                    {t("esim.optional", { defaultValue: "Optional" })}
                  </span>
                </span>
                <span className="mt-1 block text-text-muted">
                  {t("esim.create_account_body")}
                </span>
              </span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-1"
                required
              />
              <span className="text-text-muted">
                {t("esim.terms_agree_prefix")}{" "}
                <Link to={pathFor("/terms")} className="text-brand-500 underline">
                  {t("esim.terms")}
                </Link>{" "}
                {t("esim.terms_agree_and")}{" "}
                <Link to={pathFor("/privacy")} className="text-brand-500 underline">
                  {t("esim.privacy")}
                </Link>
                {t("esim.terms_agree_suffix")}
              </span>
            </label>

            {error ? <p className={errorBoxClass}>{error}</p> : null}

            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-full bg-brand-500 px-6 py-3 text-sm font-bold text-white transition enabled:hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {formState === "submitting"
                ? t("esim.redirecting_stripe")
                : t("esim.pay_stripe")}
            </button>
          </form>
        </article>
      </Container>
    </main>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="font-medium text-text-heading">
        {label}
        {required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}
